/**
 * Free gift — auto-adds a gift variant once the cart reaches a threshold, and
 * takes it back out when the cart drops below it again.
 *
 * The theme cannot discount anything: the Ajax cart API only adds a variant at
 * its own price. The item is made free by an automatic product discount on the
 * store (100% off that product, minimum purchase). This script only decides
 * whether the line is in the cart; Shopify decides what it costs.
 *
 * GUARANTEE: the shopper only ever sees the gift when it is genuinely $0 in the
 * cart. After adding it we re-read the cart, and if the line did not come back
 * free — discount missing, expired, or outranked by another product discount —
 * we take it straight back out and do not retry until the cart drops below the
 * threshold again. A shopper is never shown a "gift" they are being charged
 * for, which is the confusing case worth designing out.
 *
 * Nothing is rendered until that check has run, so there is no flash of the
 * line at full price either.
 *
 * The qualifying subtotal deliberately EXCLUDES the gift line. Counting it
 * would let the gift's own price push a cart over the threshold, which then
 * keeps it there — a latch that can never release.
 */
(function () {
  const CFG_ID = 'mw-free-gift-config';
  const DISMISS_KEY = 'mw:freeGift:dismissed';
  const UNAVAILABLE_KEY = 'mw:freeGift:unavailable';

  function readConfig() {
    const el = document.getElementById(CFG_ID);
    if (!el) return null;
    try {
      const cfg = JSON.parse(el.textContent);
      if (!cfg || !cfg.enabled) return null;
      cfg.variantId = String(cfg.variantId || '');
      cfg.threshold = parseInt(cfg.threshold, 10) || 0; // cents
      cfg.requireFree = cfg.requireFree !== false;
      if (!cfg.variantId || !cfg.threshold) return null;
      return cfg;
    } catch (e) {
      return null;
    }
  }

  class FreeGift {
    constructor(cfg) {
      this.cfg = cfg;
      this.busy = false;
      this.queued = false;
      this.lastKnownHadGift = null;
      this.ensuredScripts = new Set();

      // The cart payload the theme already has in hand is reused when it is a
      // FULL cart (/cart/change.js returns one). /cart/add.js returns only the
      // added lines, so that case still needs a read.
      // A removal click on the gift row is the only thing that counts as the
      // shopper turning the gift down.
      document.addEventListener(
        'click',
        (e) => {
          const row = e.target.closest && e.target.closest('[data-free-gift="true"]');
          if (!row) return;
          if (!e.target.closest('cart-remove-button, .cart-remove-button, [href*="quantity=0"]')) return;
          this.dismissed = true;
        },
        true
      );

      document.addEventListener('cartUpdated', (e) => this.sync(this.fullCart(e && e.detail)));
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (e) => this.sync(this.fullCart(e && e.cartData)));
      }
      // Most page loads have an empty cart; skip the read entirely then.
      if (!this.cartLooksEmpty()) this.sync();
    }

    /** Reads the server-rendered bubble rather than paying for a /cart.js. */
    cartLooksEmpty() {
      const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble, #cart-icon-bubble [data-cart-count]');
      if (!bubble) return false;
      const n = parseInt((bubble.textContent || '').replace(/[^0-9]/g, ''), 10);
      return n === 0;
    }

    get dismissed() {
      try {
        return sessionStorage.getItem(DISMISS_KEY) === '1';
      } catch (e) {
        return false;
      }
    }

    set dismissed(v) {
      try {
        if (v) sessionStorage.setItem(DISMISS_KEY, '1');
        else sessionStorage.removeItem(DISMISS_KEY);
      } catch (e) {
        /* private mode — the gift just re-adds, which is the safe direction */
      }
    }

    /** Set when the gift came back priced: stop offering it this episode. */
    get unavailable() {
      try {
        return sessionStorage.getItem(UNAVAILABLE_KEY) === '1';
      } catch (e) {
        return this._unavailable === true;
      }
    }

    set unavailable(v) {
      this._unavailable = v;
      try {
        if (v) sessionStorage.setItem(UNAVAILABLE_KEY, '1');
        else sessionStorage.removeItem(UNAVAILABLE_KEY);
      } catch (e) {
        /* falls back to the in-memory flag above */
      }
    }

    /**
     * A payload is only usable as "the cart" if it carries the whole cart, not
     * just the lines that were added. item_count is the tell.
     */
    fullCart(payload) {
      if (!payload) return null;
      const c = payload.cartData && payload.cartData.items ? payload.cartData : payload;
      if (c && Array.isArray(c.items) && typeof c.item_count === 'number') return c;
      return null;
    }

    /**
     * Subtotal of everything that is not the gift, in cents, measured on what
     * the shopper actually pays (final_line_price). This is deliberately the
     * SAME basis the offer tracker uses, so the "$X away" meter and the gift
     * appearing can never contradict each other.
     *
     * No oscillation risk: the gift is 100% off, so it contributes 0, and it is
     * excluded from the Buy-3 discount, so its presence cannot change any other
     * line's price either.
     */
    qualifyingTotal(cart) {
      return cart.items.reduce((sum, item) => {
        if (String(item.variant_id) === this.cfg.variantId) return sum;
        return sum + item.final_line_price;
      }, 0);
    }

    giftLine(cart) {
      const i = cart.items.findIndex((item) => String(item.variant_id) === this.cfg.variantId);
      return i === -1 ? null : { index: i + 1, item: cart.items[i] };
    }

    async sync(knownCart) {
      // one mutation at a time; a cart change during a mutation re-runs after
      if (this.busy) {
        this.queued = true;
        return;
      }
      this.busy = true;
      try {
        const cart = knownCart || (await this.readCart());

        const gift = this.giftLine(cart);
        const qualifies = this.qualifyingTotal(cart) >= this.cfg.threshold;

        // Dismissal is driven by an actual click on the gift row's remove
        // button (see the delegated listener), never inferred from the gift
        // going missing: emptying the cart and re-adding the same items is
        // indistinguishable from a deliberate removal, and guessing wrong
        // means the gift silently never comes back.
        if (!qualifies || !cart.item_count) {
          this.dismissed = false;
          this.unavailable = false;
        }

        this.lastKnownHadGift = !!gift;
        let changed = false;

        let sections = null;

        if (qualifies && !gift && !this.dismissed && !this.unavailable) {
          // One round trip: the add returns the new line's pricing AND the
          // rendered sections, so freeness is checked without a second read.
          const added = await this.add();
          sections = added && added.sections;
          const line = added && added.items && added.items[0];
          const notFree = line && line.final_line_price !== 0;

          if (this.cfg.requireFree && notFree) {
            const undone = await this.setQty(await this.giftIndex(), 0);
            sections = (undone && undone.sections) || sections;
            this.unavailable = true;
            this.warnNotFree(line);
          }
        } else if (!qualifies && gift) {
          const res = await this.setQty(gift.index, 0);
          sections = res && res.sections;
        } else if (gift && gift.item.quantity > 1) {
          // one gift per cart, however it got multiplied
          const res = await this.setQty(gift.index, 1);
          sections = res && res.sections;
        } else if (this.cfg.requireFree && gift && gift.item.final_line_price !== 0) {
          // it was already sitting there priced — e.g. the discount lapsed
          const res = await this.setQty(gift.index, 0);
          sections = res && res.sections;
          this.unavailable = true;
          this.warnNotFree(gift.item);
        }

        if (sections) this.render(sections);
      } catch (e) {
        // Leave the cart as the shopper left it, but do not hide a genuine
        // bug — a swallowed TypeError here once cost a debugging round.
        if (window.console && console.error) console.error('[free gift]', e);
      } finally {
        this.busy = false;
        if (this.queued) {
          this.queued = false;
          this.sync();
        }
      }
    }

    async readCart() {
      return fetch(`${window.routes ? routes.cart_url : '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      }).then((r) => r.json());
    }

    async giftIndex() {
      const cart = await this.readCart();
      const gift = this.giftLine(cart);
      return gift ? gift.index : 0;
    }

    async add() {
      const res = await fetch(`${window.routes ? routes.cart_add_url : '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: this.cfg.variantId, quantity: 1, properties: { _free_gift: 'true' } }],
          sections: this.sectionIds(),
        }),
      });
      if (!res.ok) return null;
      this.lastKnownHadGift = true;
      return res.json();
    }

    async setQty(line, quantity) {
      if (!line) return null;
      const res = await fetch(`${window.routes ? routes.cart_change_url : '/cart/change'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity, sections: this.sectionIds() }),
      });
      if (!res.ok) return null;
      this.lastKnownHadGift = quantity > 0;
      return res.json();
    }

    warnNotFree(line) {
      if (!line || !window.console || !console.warn) return;
      console.warn(
        '[free gift] "' +
          line.title +
          '" came back at ' +
          (line.final_line_price / 100).toFixed(2) +
          ' instead of 0, so it was removed rather than shown as a paid gift. ' +
          'Check that an automatic product discount gives 100% off this product ' +
          'at this cart total, and that no other product discount outranks it.'
      );
    }

    /**
     * Render straight from the sections the mutation already returned — no
     * extra requests, and nothing is painted until the free check has run.
     */
    render(sectionsHtml) {
      if (!sectionsHtml) return;
      this.sections().forEach((s) => {
        const html = sectionsHtml[s.section];
        if (!html) return;
        const target = document.querySelector(s.selector);
        if (!target) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const src = doc.querySelector(s.inner) || doc.querySelector(s.selector) || doc.body;
        if (!src) return;
        const dest = target.querySelector(s.inner) || target;
        dest.innerHTML = src.innerHTML;
        this.runScripts(dest);
      });
      document.dispatchEvent(new CustomEvent('mw:freeGiftUpdated'));
    }

    /**
     * Scripts injected via innerHTML never execute. The cart drawer markup
     * carries the offer tracker's <script src>, so replacing the drawer's
     * contents was silently killing customElements.define('offer-progress')
     * and leaving the three offer meters blank for the rest of the page life.
     * Re-insert them as fresh nodes so they run. Each guards itself against
     * double-definition, so re-running is safe.
     */
    runScripts(container) {
      container.querySelectorAll('script[src]').forEach((tag) => {
        const src = tag.src;
        if (!src || this.ensuredScripts.has(src)) return;
        this.ensuredScripts.add(src);

        // Already live elsewhere in the document (outside what we replaced)?
        // Then it ran on page load and must not run twice — re-executing a
        // customElements.define() throws.
        if (document.querySelector(`head script[src="${src}"], body > script[src="${src}"]`)) return;

        // Re-add at document level, so later drawer re-renders cannot destroy
        // it again. Inert copies left inside the drawer markup are harmless.
        const fresh = document.createElement('script');
        fresh.src = src;
        fresh.defer = true;
        fresh.setAttribute('data-mw-restored', '');
        document.head.appendChild(fresh);
      });
    }

    sectionIds() {
      return this.sections()
        .map((s) => s.section)
        .join(',');
    }

    sections() {
      const out = [];
      if (document.querySelector('cart-drawer')) {
        out.push({ section: 'cart-drawer', selector: '#CartDrawer', inner: '.drawer__inner' });
      }
      const mainCart = document.getElementById('main-cart-items');
      if (mainCart) {
        out.push({ section: mainCart.dataset.id, selector: '#main-cart-items', inner: '.js-contents' });
      }
      const footer = document.getElementById('main-cart-footer');
      if (footer) {
        out.push({ section: footer.dataset.id, selector: '#main-cart-footer', inner: '.js-contents' });
      }
      out.push({ section: 'cart-icon-bubble', selector: '#cart-icon-bubble', inner: '.shopify-section' });
      return out;
    }

  }

  function boot() {
    const cfg = readConfig();
    if (cfg) new FreeGift(cfg);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
